import { z } from "zod";
// 🔴 US-E24.F33-0447 (@UC9713) — PHẠM VI THÀNH VIÊN đã có luật ở lịch định kỳ (@UC9616, QĐ PO
// 08/08/2026). Import lại hằng biên thay vì khai bản thứ hai: hai route anh em (`/collections` một
// lần và `/collections/schedules` định kỳ) phải nhận CÙNG một danh sách trắng với CÙNG luật, nếu
// không thì client gửi đúng cho route này và sai cho route kia. Đây là quan hệ MỘT CHIỀU
// (collections → collection-schedules), không có vòng: `collection-schedules.ts` chỉ import `zod`.
import {
  COLLECTION_DESCRIPTION_MAX,
  MEMBER_IDS_DUPLICATE_MESSAGE,
  MEMBER_IDS_EMPTY_MESSAGE,
  MEMBER_IDS_TOO_MANY_MESSAGE,
  SCHEDULE_MEMBER_IDS_MAX,
} from "./collection-schedules.js";

/* ===========================================================================
 * POST /v1/funds/:id/collections — Create Ad-hoc Collection (đợt thu đột xuất one-time)
 * (US-E6.F2-0037 / @UC0039, Phase 7 Collections — @gate money,rbac).
 *
 * Cross-check index §3/§5 (PO chốt): tạo Collection (kind=adhoc, status=open, recurrence=null) +
 * sinh member_dues `pending` cho mọi member ACTIVE đồng bộ. KHÔNG schedule, KHÔNG recurrence,
 * KHÔNG ghi ledger (member_dues ở `pending` — B2). Mirror collection-schedules.ts (amount STRING
 * BigInt — B1, `.strict()` chặn over-post, refine BIGINT-overflow).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: `amountMinor` truyền dạng STRING số nguyên (chống mất chính xác JSON number >
 *    2^53). Server parse → BigInt. KHÔNG float ở bất kỳ tầng nào.
 *  - C5 `.strict()` chặn over-post (KHÔNG nhận currency/fundId/kind/status từ body — currency kế
 *    thừa fund, fundId từ :id param, kind/status do server set).
 *  - C4 RBAC OWNER/ADMIN/TREASURER (@RequireFundRoles trên FundAccessGuard); Member → 403;
 *    non-member/cross-tenant/non-UUID → 404 đồng nhất.
 *  - Validate biên: số tiền > 0 (AC3), hạn đóng không quá khứ (AC2 — kiểm tz ở service/domain).
 * ========================================================================= */

/** Loại đợt thu (khớp Collection.kind). */
export const COLLECTION_KIND = {
  PERIODIC: "periodic",
  ADHOC: "adhoc",
  SETTLEMENT: "settlement",
} as const;

export type CollectionKind = (typeof COLLECTION_KIND)[keyof typeof COLLECTION_KIND];

/** Trạng thái đợt thu (khớp Collection.status). */
export const COLLECTION_STATUS = {
  OPEN: "open",
  CLOSED: "closed",
} as const;

export type CollectionStatus = (typeof COLLECTION_STATUS)[keyof typeof COLLECTION_STATUS];

/**
 * Schema của trạng thái VÒNG ĐỜI đợt thu (US-E24.F2-0351-S03 · @UC9567) — một nguồn sự thật để
 * server parse fail-loud và client map. Khớp CHECK `collections_status_check` ở DB.
 */
export const collectionStatusSchema = z.enum([
  COLLECTION_STATUS.OPEN,
  COLLECTION_STATUS.CLOSED,
]);

/** Giới hạn tiêu đề đợt thu (đủ cho tiêu đề; chống over-long). */
export const ADHOC_TITLE_MAX_LENGTH = 100;

/**
 * Số chữ số tối đa cho amount (SEC MED-1): PG BIGINT max = 9223372036854775807 (19 chữ số). Chuỗi
 * số dài hơn parse BigInt OK nhưng ghi DB ném P2020 → 500. .max(19) chặn ≥20 chữ số, NHƯNG ~8.6% giá
 * trị 19 chữ số (9223372036854775808..9999999999999999999) vẫn vượt BIGINT → cần refine giá trị (dưới).
 */
export const ADHOC_AMOUNT_MAX_DIGITS = 19;

/** Trần PG signed BIGINT (9_223_372_036_854_775_807). amount > trần → 400 (chống overflow P2020→500). */
export const ADHOC_PG_BIGINT_MAX = 9223372036854775807n;

/** Thông điệp lỗi user-facing — một nguồn sự thật api ↔ mobile (khớp AC). */
export const ADHOC_AMOUNT_REQUIRED_MESSAGE = "Số tiền bắt buộc";
export const ADHOC_AMOUNT_INVALID_MESSAGE = "Số tiền phải là số nguyên (đơn vị nhỏ nhất)";
export const ADHOC_DUE_DATE_INVALID_MESSAGE = "Hạn đóng không hợp lệ (định dạng YYYY-MM-DD)";

/** Regex 'YYYY-MM-DD' (date-only). Parse → Date UTC midnight ở service. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** Số nguyên dạng string (cho amount BigInt) — không dấu chấm, không số mũ, không dấu âm. */
const INTEGER_STRING_PATTERN = /^\d+$/;

/** True nếu chuỗi 'YYYY-MM-DD' là một ngày dương lịch THẬT (chặn '2026-02-30'). */
function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split("-").map((p) => Number.parseInt(p, 10));
  if (y === undefined || m === undefined || d === undefined) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
  );
}

/**
 * Body cho POST /v1/funds/:id/collections.
 *  - title: trim non-empty ≤100.
 *  - amountMinor: STRING số nguyên dương (B1 BigInt — server parse). ""/âm/float/overflow → 400 (AC3).
 *  - dueDate: 'YYYY-MM-DD' (ngày dương lịch THẬT). Quá khứ → 400 (AC2 — kiểm tz ở service/domain).
 *  - currency CỐ Ý VẮNG: kế thừa fund.currency (server đọc — KHÔNG nhận từ body).
 *  - `.strict()` chặn over-post (currency/fundId/kind/status).
 */
export const createAdhocCollectionRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(ADHOC_TITLE_MAX_LENGTH),
    amountMinor: z
      .string()
      .trim()
      .min(1, ADHOC_AMOUNT_REQUIRED_MESSAGE)
      .max(ADHOC_AMOUNT_MAX_DIGITS, ADHOC_AMOUNT_INVALID_MESSAGE)
      .regex(INTEGER_STRING_PATTERN, ADHOC_AMOUNT_INVALID_MESSAGE)
      // SEC MED-1 (residual): .max(19) còn lọt giá trị 19 chữ số vượt BIGINT (vd 9999999999999999999)
      // → ghi DB P2020 → 500. Refine giá trị THẬT ≤ trần BIGINT. Bọc try/catch: chuỗi không-số →
      // BigInt() ném ⇒ trả false (invalid), KHÔNG throw.
      .refine((v) => {
        try {
          return BigInt(v) <= ADHOC_PG_BIGINT_MAX;
        } catch {
          return false;
        }
      }, ADHOC_AMOUNT_INVALID_MESSAGE),
    dueDate: z
      .string()
      .trim()
      .regex(ISO_DATE_PATTERN, ADHOC_DUE_DATE_INVALID_MESSAGE)
      .refine(isRealCalendarDate, ADHOC_DUE_DATE_INVALID_MESSAGE),
    /**
     * 🔴 @UC9713 — PHẠM VI THÀNH VIÊN: danh sách **TRẮNG** những `group_members.id` ĐƯỢC THU.
     *
     * **VẮNG ⇒ mọi member `active`** như trước (§3.8 *"default ⇒ TẮT ⇒ 0 REGRESS"*). Hàng nghìn đợt
     * thu cũ, và đường AI `col-collection-executor.ts`, không gửi field này ⇒ không đổi một hành vi nào.
     *
     * **`[]` ⇒ 400**, KHÔNG đọc thành "chọn tất cả": một client tính ra tập rỗng do lỗi mà server
     * hiểu thành "cả nhóm" là đi đòi tiền 40 người vì một mảng rỗng.
     * ⚠️ Khác với cột DB `collection_schedules.member_ids` (`NOT NULL DEFAULT '{}'`) nơi `[]` ĐÚNG LÀ
     * "không giới hạn" — đó là default của tầng LƯU cho lịch cũ, không phải ngữ nghĩa của REQUEST.
     *
     * **Trùng ⇒ 400, KHÔNG tự dedupe** (xem `superRefine` dưới): tự dedupe là im lặng sửa ý người
     * dùng trên chính danh sách quyết định AI PHẢI ĐÓNG TIỀN.
     *
     * 🔴 `excludeMemberIds` (danh sách ĐEN) **cố ý không tồn tại** — mirror @UC9616. Hai kiểu phạm vi
     * hành xử KHÁC NHAU với người vào nhóm sau ⇒ có cả hai là có hai luật tiền song song. `.strict()`
     * biến điều đó thành đảm bảo của KIỂU, 0 dòng validate.
     */
    memberIds: z
      .array(z.string().uuid())
      .min(1, MEMBER_IDS_EMPTY_MESSAGE)
      .max(SCHEDULE_MEMBER_IDS_MAX, MEMBER_IDS_TOO_MANY_MESSAGE)
      .optional(),
    /**
     * US-E24.F36-0450 (@UC0039) — MÔ TẢ khoản thu: "tiền này dùng vào việc gì".
     *
     * **OPTIONAL ở API, dù form mobile đánh dấu bắt buộc.** Hai tầng khác nhau và cố ý không đồng
     * bộ: `.strict()` + optional giữ cho hàng nghìn đợt thu cũ và đường AI `col-collection-executor`
     * (không gửi field này) chạy y nguyên — §3.8 *"default ⇒ TẮT ⇒ 0 REGRESS"*. Muốn ép người dùng
     * điền thì ép ở FORM, nơi có ngữ cảnh để nói "vì sao cần"; ép ở contract là làm hỏng mọi client
     * cũ để đổi lấy một ràng buộc mà UI vốn đã bảo đảm.
     *
     * Trần 1000 ký tự theo tiền lệ `Expense.description` (`expenses.ts:259`) — cùng vai trò, cùng
     * kiểu người dùng gõ, nên cùng trần. `.trim()` trước khi đo để "   " không thành mô tả hợp lệ.
     */
    description: z.string().trim().max(COLLECTION_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    // 🔴 So TRÙNG phải KHÔNG phân biệt hoa/thường — `z.string().uuid()` khớp case-insensitive, nên
    // `["7C9E…", "7c9e…"]` (CÙNG một người) lọt qua phép so chuỗi thô. Service chuẩn hoá lowercase
    // rồi mới kiểm sở hữu ⇒ `memberIds.length = 2` mà `groupMember.count` = 1 ⇒ **404 "không thuộc
    // nhóm"** cho một thành viên hoàn toàn có thật: vừa sai mã lỗi so với 400 đã khai, vừa bắt người
    // dùng đi tìm một thành viên lạ không hề tồn tại. (HIGH-1 của @UC9616, cùng cách vá.)
    if (
      body.memberIds &&
      new Set(body.memberIds.map((id) => id.toLowerCase())).size !== body.memberIds.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["memberIds"],
        message: MEMBER_IDS_DUPLICATE_MESSAGE,
      });
    }
  });

export type CreateAdhocCollectionRequest = z.infer<typeof createAdhocCollectionRequestSchema>;

/**
 * Shape response cho POST collections (201).
 *  - collection: đợt vừa tạo (id/fundId/title/kind/status/amount-as-string/currency/dueDate).
 *  - duesCreated: số member_dues sinh (mọi member ACTIVE).
 *  - amountPerMemberMinor trả dạng STRING (B1 — KHÔNG number để tránh mất chính xác). `.strict()`.
 */
export const adhocCollectionResponseSchema = z
  .object({
    collection: z
      .object({
        id: z.string().uuid(),
        fundId: z.string().uuid(),
        title: z.string(),
        kind: z.literal(COLLECTION_KIND.ADHOC),
        status: z.literal(COLLECTION_STATUS.OPEN),
        amountPerMemberMinor: z.string().regex(INTEGER_STRING_PATTERN),
        currency: z.string().length(3),
        dueDate: z.string().regex(ISO_DATE_PATTERN),
      })
      .strict(),
    duesCreated: z.number().int().nonnegative(),
  })
  .strict();

export type AdhocCollectionResponse = z.infer<typeof adhocCollectionResponseSchema>;

/* ===========================================================================
 * GET /v1/funds/:id/collections?member_id=me — View My Collection Periods to Pay
 * (US-E6.F3-0038 / @UC0057, Phase 7 Collections — @gate rbac). READ-only.
 *
 * Cross-check index §3 (PO chốt): trả các đợt thu ACTIVE của fund mà CALLER có member_due (enrolled)
 * + trạng thái đóng tiền lấy TRỰC TIẾP từ member_dues.status (KHÔNG recompute — invariant story
 * USER_STORIES.md:1542). member_id CHỈ chấp nhận 'me' (self) — server resolve groupMemberId của
 * caller từ FundAccessGuard ctx (KHÔNG tin member id client gửi — chống IDOR).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: amountMinor trả dạng STRING số nguyên (KHÔNG float/number > 2^53).
 *  - C4 RBAC + no-leak: FundAccessGuard bare (mọi member ACTIVE đọc được); non-member → 404 đồng nhất
 *    (repo 404-uniformity — chống IDOR, thoả intent "không lộ dữ liệu quỹ" của AC6).
 *  - Status từ nguồn: `status` = member_dues.status nguyên gốc. `isOverdue`/`isNearDeadline` là cờ
 *    hiển thị server-derive (KHÔNG ghi DB), KHÔNG đổi `status`.
 *  - Read-only: KHÔNG ghi ledger / KHÔNG mutate member_dues.
 * ========================================================================= */

/** Trạng thái member_due (khớp MemberDue.status — schema.prisma:493). NGUỒN sự thật render badge. */
export const MEMBER_DUE_STATUS = {
  PENDING: "pending",
  AWAITING_CONFIRMATION: "awaiting_confirmation",
  PAID: "paid",
  // US-E16.F4-0185 (@UC9241): đóng MỘT PHẦN — còn nợ = amountMinor − paidAmountMinor (partial-pay).
  PARTIALLY_PAID: "partially_paid",
  WAIVED: "waived",
  OVERDUE: "overdue",
  PAID_ON_BEHALF: "paid_on_behalf",
} as const;

export type MemberDueStatus = (typeof MEMBER_DUE_STATUS)[keyof typeof MEMBER_DUE_STATUS];

export const memberDueStatusSchema = z.enum([
  MEMBER_DUE_STATUS.PENDING,
  MEMBER_DUE_STATUS.AWAITING_CONFIRMATION,
  MEMBER_DUE_STATUS.PAID,
  MEMBER_DUE_STATUS.PARTIALLY_PAID,
  MEMBER_DUE_STATUS.WAIVED,
  MEMBER_DUE_STATUS.OVERDUE,
  MEMBER_DUE_STATUS.PAID_ON_BEHALF,
]);

/**
 * Ngưỡng "sắp đến hạn" (AC3): dueDate ≤ N ngày tính từ now (tz Asia/Ho_Chi_Minh) AND status=pending.
 * MẶC ĐỊNH 3 ngày (PO §6 — nhất quán toàn cụm E6). Đặt tên tránh magic number; server import.
 */
export const NEAR_DEADLINE_DAYS = 3;

/** Giá trị duy nhất hợp lệ cho query `member_id` — self/me (KHÔNG cho xem member khác, chống IDOR). */
export const MEMBER_ID_SELF = "me";

/** Regex 'YYYY-MM-DD' (date-only) — dueDate trả về. */
const PERIOD_ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const PERIOD_INTEGER_STRING_PATTERN = /^\d+$/;

/**
 * 1 đợt thu của tôi (AC1). status TỪ member_dues.status; isOverdue/isNearDeadline server-derive.
 *  - collectionId/title/currency/dueDate mô tả đợt; amountMinor là số tiền của member_due (STRING B1).
 *  - dueDate có thể null (đợt không đặt hạn).
 */
export const myCollectionPeriodItemSchema = z
  .object({
    collectionId: z.string().uuid(),
    title: z.string(),
    amountMinor: z.string().regex(PERIOD_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    dueDate: z.string().regex(PERIOD_ISO_DATE_PATTERN).nullable(),
    status: memberDueStatusSchema,
    /**
     * 🔴 US-E24.F2-0351 H3 (QĐ PO 07/08 · @UC9568) — trạng thái VÒNG ĐỜI CỦA ĐỢT (`open | closed`),
     * nguồn cho chip "Đã đóng". Tên KHÁC `status` là CỐ Ý (mirror `collectionDetail`): `status` ở
     * đây là member_due CỦA CALLER. Trước H3 danh sách ẨN HẲN đợt `closed` trong khi tổng vẫn cộng
     * dues của nó ⇒ thành viên bị đòi tiền cho một đợt KHÔNG NHÌN THẤY ĐƯỢC. Nay đợt vẫn hiện và
     * mang theo trạng thái của chính nó.
     */
    collectionStatus: collectionStatusSchema,
    isOverdue: z.boolean(),
    isNearDeadline: z.boolean(),
  })
  .strict();

export type MyCollectionPeriodItem = z.infer<typeof myCollectionPeriodItemSchema>;

/**
 * Envelope { periods: [...] } (getJson client nhận Map — KHÔNG array trần). Empty → { periods: [] }.
 *
 * 🔴🔴 ĐỌC KỸ TÊN ENDPOINT — `GET /v1/funds/{id}/collections` **KHÔNG phải danh sách cấp quỹ**.
 * Đây là **member-self**: chỉ trả các đợt thu ĐANG MỞ mà **CHÍNH caller** có `member_due`
 * (`view-collection-periods.service.ts:47-49` lọc `groupMemberId`; invariant `:24` *"IDOR: chỉ trả
 * due của caller"*). Caller không có due nào ⇒ `{ periods: [] }`, **không** rơi về danh sách của quỹ.
 * Vai OWNER/ADMIN/TREASURER **cũng chỉ thấy đợt của chính họ** — vai cao không nới phạm vi endpoint này.
 * `member_id` chỉ chấp nhận `'me'`/self; giá trị khác bị **BỎ QUA** (không cho xem member khác).
 *
 * ⚠️ `audit/SCREEN_API_MAP.md:110` ghi *"bản cũ map nhầm nghĩa"* — **đã có người hiểu sai một lần
 * rồi**. Biến endpoint này thành "danh sách của quỹ" = **mở due của người khác cho mọi member**,
 * đúng thứ `collection-period-status` (aggregate-only) cố ý chặn.
 *
 * ⇒ Cần **tiến độ đợt thu ở cấp QUỸ** (màn C1: mọi đợt của quỹ + "đã đóng X/Y" + "đã thu"): dùng
 * `GET /v1/funds/{id}/collection-progress` (`collection-progress.ts`, @UC9623) — endpoint RIÊNG, có
 * redaction tiền theo vai. **KHÔNG sửa endpoint này để lấy dữ liệu cấp quỹ.**
 * (US-E24.F18-0367-S02 · @UC9625 — quyết định (B): chỉ ghi rõ ngữ nghĩa, không thêm endpoint thứ ba.)
 */
export const myCollectionPeriodsResponseSchema = z
  .object({
    periods: z.array(myCollectionPeriodItemSchema),
  })
  .strict();

export type MyCollectionPeriodsResponse = z.infer<typeof myCollectionPeriodsResponseSchema>;

/* ===========================================================================
 * GET /v1/collections/:id — View Detail & Payment QR
 * (US-E6.F3-0039 / @UC0057, Phase 7 Collections — @gate rbac,pii). READ-only.
 *
 * Cross-check index §3 / USER_STORIES.md:1584 (PO chốt): trả chi tiết đợt thu CỦA CALLER (tên, số tiền,
 * hạn đóng, thông tin ngân hàng quỹ — PII member-only) + member_due của caller + các CỜ HIỂN THỊ:
 *  - canReportTransfer = (payment_confirmation_mode === 'two_party') AND status ∈ {pending, overdue}.
 *    Mode admin_one_party → false + confirmationHint (mobile map sang "Thủ quỹ sẽ ghi nhận…"). AC5.
 *  - canCancelReport = status === awaiting_confirmation (AC6 — client render nút "Huỷ báo CK"; hành vi
 *    huỷ thật ⇐ story 0040).
 *  - status paid/paid_on_behalf → isConfirmed=true + confirmedAt (AC4 — client ẩn QR/nút).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: amountMinor trả dạng STRING số nguyên (KHÔNG float/number > 2^53).
 *  - C4 RBAC + C2 PII: bank info CHỈ trả cho member ACTIVE của quỹ (CollectionAccessGuard); non-member/
 *    collection-không-tồn-tại/non-UUID → 404 đồng nhất (repo 404-uniformity — chống IDOR).
 *  - Status từ nguồn: status = member_dues.status nguyên gốc (KHÔNG recompute). Cờ canReportTransfer/
 *    canCancelReport/isConfirmed là server-derive (KHÔNG ghi DB).
 *  - Read-only: KHÔNG ghi ledger / KHÔNG mutate member_dues / KHÔNG tạo PaymentConfirmation.
 * ========================================================================= */

/** Key gợi ý cho client map text khi config admin_one_party (KHÔNG nhúng text VN vào API — i18n ở mobile). */
export const COLLECTION_CONFIRMATION_HINT = {
  /** Mobile map → "Thủ quỹ sẽ ghi nhận khi nhận được tiền" (AC5). */
  ADMIN_RECORDS: "admin_records",
} as const;

export type CollectionConfirmationHint =
  (typeof COLLECTION_CONFIRMATION_HINT)[keyof typeof COLLECTION_CONFIRMATION_HINT];

export const collectionConfirmationHintSchema = z.enum([
  COLLECTION_CONFIRMATION_HINT.ADMIN_RECORDS,
]);

/** Thông tin ngân hàng quỹ trả về màn chi tiết (PII member-only). accountNo numeric — Copy STK (AC2). */
const COLLECTION_DETAIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const COLLECTION_DETAIL_INTEGER_STRING_PATTERN = /^\d+$/;

export const collectionDetailBankInfoSchema = z
  .object({
    bankBin: z.string(),
    accountNo: z.string(),
    accountName: z.string(),
  })
  .strict();

export type CollectionDetailBankInfo = z.infer<typeof collectionDetailBankInfoSchema>;

/**
 * Chi tiết đợt thu + member_due của caller + cờ hiển thị (AC1/AC4/AC5/AC6).
 *  - bankInfo có thể null nếu quỹ chưa cấu hình tài khoản (client điều hướng / ẩn QR).
 *  - confirmedAt: ISO timestamp khi member_due đã paid (AC4) — null nếu chưa.
 *  - confirmationHint: chỉ set khi config admin_one_party (AC5) — null các mode khác.
 */
export const collectionDetailResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    title: z.string(),
    amountMinor: z.string().regex(COLLECTION_DETAIL_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    dueDate: z.string().regex(COLLECTION_DETAIL_DATE_PATTERN).nullable(),
    /**
     * 🔴 `status` ở đây là trạng thái **MEMBER_DUE CỦA CALLER** (đã đóng hay chưa) — KHÔNG phải
     * trạng thái đợt thu. Tên này có trước và không đổi được nữa (client đang đọc).
     */
    status: memberDueStatusSchema,
    /**
     * 🔴 US-E24.F2-0351-S03 (@UC9567) — trạng thái VÒNG ĐỜI CỦA ĐỢT (`open | closed`), nguồn cho chip
     * "Đã hoàn thành". Đặt tên KHÁC `status` là CỐ Ý: hai nghĩa khác nhau tuyệt đối không được dùng
     * chung một tên trong cùng một object — đó đúng loại nhầm lẫn CLAUDE.md §7 gọi là "bẫy đặt tên
     * nguy hiểm nhất". Một due `paid` trong một đợt `open` và một due `pending` trong đợt `closed`
     * là hai tình huống khác hẳn nhau; gộp tên là mời người đọc kết luận sai về tiền.
     */
    collectionStatus: z.enum([COLLECTION_STATUS.OPEN, COLLECTION_STATUS.CLOSED]),
    bankInfo: collectionDetailBankInfoSchema.nullable(),
    canReportTransfer: z.boolean(),
    canCancelReport: z.boolean(),
    isConfirmed: z.boolean(),
    confirmedAt: z.string().datetime().nullable(),
    confirmationHint: collectionConfirmationHintSchema.nullable(),
  })
  .strict();

export type CollectionDetailResponse = z.infer<typeof collectionDetailResponseSchema>;

/* ===========================================================================
 * POST /v1/member-dues/:id/confirmations — Report Payment + Upload Proof
 * (US-E6.F3-0040 / @UC0057, Phase 7 Collections — @gate money,pii).
 *
 * Cross-check index §3 / USER_STORIES.md:1603 (PO chốt): member báo CK (tap "Tôi đã CK") →
 * tạo PaymentConfirmation(status=submitted) + claimed_amount_minor = amount của due →
 * member_due → awaiting_confirmation. Admin nhận notification (defer push P8.5).
 *
 * KHÔNG POST LEDGER ở task này — post CONTRIBUTION xảy ra khi admin duyệt (task 0041).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - B1 tiền BigInt: claimedAmountMinor trả dạng STRING số nguyên (KHÔNG float).
 *  - C4 RBAC: CHỈ owner của member_due báo được (resolver từ guard ctx — chống IDOR).
 *  - C5 `.strict()`: chặn over-post (KHÔNG nhận memberDueId/submittedBy/status từ body).
 *  - FAIL-CLOSED mode: admin_one_party → 403 (member KHÔNG báo khi mode 1-đầu). unknown mode
 *    → lưu awaiting_confirmation (KHÔNG auto-confirm mù).
 *  - PII proof: magic-byte sniff (KHÔNG tin content-type), size ≤10MB, R2 storage.
 *  - NO LEDGER: KHÔNG ghi ledger_transaction ở task này (assert trong test).
 * ========================================================================= */

/** Trạng thái PaymentConfirmation (khớp PaymentConfirmation.status — schema.prisma:515). */
export const PAYMENT_CONFIRMATION_STATUS = {
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

export type PaymentConfirmationStatus =
  (typeof PAYMENT_CONFIRMATION_STATUS)[keyof typeof PAYMENT_CONFIRMATION_STATUS];

export const paymentConfirmationStatusSchema = z.enum([
  PAYMENT_CONFIRMATION_STATUS.SUBMITTED,
  PAYMENT_CONFIRMATION_STATUS.APPROVED,
  PAYMENT_CONFIRMATION_STATUS.REJECTED,
]);

/** Giới hạn kích thước proof = 10MB (story: ≤10MB, 1 ảnh). */
export const PROOF_MAX_BYTES = 10 * 1024 * 1024;

/** Content-type ảnh được chấp nhận cho proof (jpeg/png/webp — reuse avatar pattern). */
export const ALLOWED_PROOF_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export type ProofContentType = (typeof ALLOWED_PROOF_CONTENT_TYPES)[number];

/** Thông điệp lỗi user-facing — một nguồn sự thật api ↔ mobile (khớp AC). */
export const PROOF_TOO_LARGE_MESSAGE = "Ảnh quá lớn. Vui lòng chọn ảnh dưới 10MB.";
export const PROOF_UPLOAD_FAILED_MESSAGE = "Upload thất bại. Thử lại hoặc bỏ qua ảnh.";
export const PROOF_INVALID_FORMAT_MESSAGE =
  "Định dạng ảnh không hỗ trợ. Chỉ chấp nhận JPEG, PNG hoặc WebP.";
export const REPORT_MODE_NOT_ALLOWED_MESSAGE =
  "Quỹ không cho phép thành viên tự báo chuyển khoản.";
export const REPORT_STATUS_NOT_ALLOWED_MESSAGE =
  "Không thể báo chuyển khoản cho khoản thu này.";

/**
 * Body cho POST /v1/member-dues/:id/confirmations.
 * - proof: file ảnh (optional) — validate tại biên (magic-byte, ≤10MB).
 * - `.strict()` chặn over-post (KHÔNG nhận claimedAmountMinor/submittedBy/status từ body —
 *   amount kế thừa member_due, submittedBy từ token, status=submitted server set).
 *
 * NOTE: body chỉ chứa metadata nếu cần (vd ghi chú); file upload qua multipart. Hiện không cần
 * body field nào → body empty object `.strict()` chặn over-post.
 */
export const reportPaymentRequestSchema = z.object({}).strict();

export type ReportPaymentRequest = z.infer<typeof reportPaymentRequestSchema>;

/** Regex số nguyên dạng string (cho amount BigInt). */
const REPORT_PAYMENT_INTEGER_STRING_PATTERN = /^\d+$/;

/**
 * Shape response cho POST /v1/member-dues/:id/confirmations (201).
 *  - confirmationId: PaymentConfirmation vừa tạo.
 *  - memberDueId: liên kết MemberDue.
 *  - claimedAmountMinor: STRING BigInt (B1).
 *  - status: "submitted" (always).
 *  - proofUrl: R2 URL hoặc null (báo không ảnh).
 *  - newDueStatus: "awaiting_confirmation" (sau khi báo thành công).
 */
export const reportPaymentResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    claimedAmountMinor: z.string().regex(REPORT_PAYMENT_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: z.literal(PAYMENT_CONFIRMATION_STATUS.SUBMITTED),
    proofUrl: z.string().url().nullable(),
    newDueStatus: z.literal(MEMBER_DUE_STATUS.AWAITING_CONFIRMATION),
    createdAt: z.string().datetime(),
  })
  .strict();

export type ReportPaymentResponse = z.infer<typeof reportPaymentResponseSchema>;

/* ===========================================================================
 * DELETE /v1/member-dues/:id/confirmations — Cancel Report (huỷ báo CK)
 * (US-E6.F3-0040 AC6 / @UC0057, Phase 7 Collections).
 *
 * Khi status = awaiting_confirmation (chưa admin duyệt) → huỷ báo CK → member_due về pending.
 * PaymentConfirmation KHÔNG xoá (audit trail) — chỉ cập nhật status → rejected (self-cancel).
 * ========================================================================= */

export const cancelReportResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    newDueStatus: z.literal(MEMBER_DUE_STATUS.PENDING),
    cancelledConfirmationId: z.string().uuid(),
  })
  .strict();

export type CancelReportResponse = z.infer<typeof cancelReportResponseSchema>;
